import {
  CITY_CENTER,
  COINS_PER_XP,
  COIN_LEVEL_EXP,
  LEVEL_RAMP_START,
  LEVEL_RAMP_STEP,
  MAX_ENEMY_LEVEL,
} from './constants';
import type { EnemyDef, EnemyKind, PoiKind } from './types';
/**
 * Monsters. `melee` types charge and bite; `ranged` types keep their distance
 * and shoot. Stats scale with the monster's level, which rises the further you
 * travel from the world centre.
 */
export const ENEMIES: Record<EnemyKind, EnemyDef> = {
  slime: {
    id: 'slime',
    name: 'Slime',
    behavior: 'melee',
    color: '#57c9a4',
    accent: '#b8f5e2',
    radius: 16,
    speed: 92,
    aggroRange: 520,
    preferredRange: 0,
    attack: { range: 30, cooldown: 1.35, windup: 0.28 },
    base: { maxHp: 52, attack: 8, defense: 2 },
    perLevel: { maxHp: 16, attack: 2.1, defense: 1.1 },
    xpBase: 11,
  },
  boar: {
    id: 'boar',
    name: 'Boar',
    behavior: 'melee',
    color: '#a8703f',
    accent: '#f2d3ac',
    radius: 19,
    speed: 152,
    aggroRange: 640,
    preferredRange: 0,
    attack: { range: 34, cooldown: 1.1, windup: 0.22 },
    base: { maxHp: 68, attack: 11, defense: 3 },
    perLevel: { maxHp: 19, attack: 2.6, defense: 1.2 },
    xpBase: 15,
  },
  imp: {
    id: 'imp',
    name: 'Imp',
    behavior: 'ranged',
    color: '#c0392b',
    accent: '#ff9b7a',
    radius: 15,
    speed: 128,
    aggroRange: 700,
    preferredRange: 300,
    attack: {
      range: 400,
      cooldown: 1.9,
      windup: 0.45,
      projectile: { speed: 380, radius: 7, life: 1.6, color: '#ff7a55' },
    },
    base: { maxHp: 46, attack: 10, defense: 1 },
    perLevel: { maxHp: 13, attack: 2.7, defense: 0.7 },
    xpBase: 17,
  },
  wisp: {
    id: 'wisp',
    name: 'Wisp',
    behavior: 'ranged',
    color: '#4bb7f0',
    accent: '#d5f2ff',
    radius: 14,
    speed: 146,
    aggroRange: 760,
    preferredRange: 340,
    attack: {
      range: 440,
      cooldown: 1.5,
      windup: 0.5,
      projectile: { speed: 470, radius: 8, life: 1.4, color: '#8fe3ff' },
    },
    base: { maxHp: 40, attack: 13, defense: 0 },
    perLevel: { maxHp: 11, attack: 3.1, defense: 0.6 },
    xpBase: 20,
  },
  brute: {
    id: 'brute',
    name: 'Brute',
    behavior: 'melee',
    color: '#7d5ba6',
    accent: '#d6b8ff',
    radius: 27,
    speed: 112,
    aggroRange: 700,
    preferredRange: 0,
    attack: { range: 48, cooldown: 1.65, windup: 0.42 },
    base: { maxHp: 165, attack: 20, defense: 8 },
    perLevel: { maxHp: 42, attack: 4.2, defense: 2.4 },
    xpBase: 46,
  },
  /* ------------------------------------------- mid and late game roster */
  wolf: {
    id: 'wolf',
    name: 'Wolf',
    behavior: 'melee',
    color: '#8a8f98',
    accent: '#d8dde8',
    radius: 17,
    speed: 176,
    aggroRange: 680,
    preferredRange: 0,
    attack: { range: 32, cooldown: 0.95, windup: 0.18 },
    base: { maxHp: 55, attack: 12, defense: 2 },
    perLevel: { maxHp: 15, attack: 2.3, defense: 0.8 },
    xpBase: 16,
  },
  skeleton: {
    id: 'skeleton',
    name: 'Skeleton',
    behavior: 'melee',
    color: '#cfc9b0',
    accent: '#f2ecd6',
    radius: 18,
    speed: 120,
    aggroRange: 720,
    preferredRange: 0,
    attack: { range: 42, cooldown: 1.35, windup: 0.32 },
    base: { maxHp: 85, attack: 15, defense: 5 },
    perLevel: { maxHp: 24, attack: 3.0, defense: 1.4 },
    xpBase: 26,
  },
  cultist: {
    id: 'cultist',
    name: 'Cultist',
    behavior: 'ranged',
    color: '#7d4fb0',
    accent: '#d3b6ff',
    radius: 16,
    speed: 122,
    aggroRange: 780,
    preferredRange: 330,
    attack: {
      range: 430,
      cooldown: 2.0,
      windup: 0.5,
      projectile: { speed: 400, radius: 8, life: 1.6, color: '#c58bff' },
    },
    base: { maxHp: 70, attack: 16, defense: 2 },
    perLevel: { maxHp: 18, attack: 3.4, defense: 0.9 },
    xpBase: 28,
  },
  golem: {
    id: 'golem',
    name: 'Golem',
    behavior: 'melee',
    color: '#7f8590',
    accent: '#c7cede',
    radius: 30,
    speed: 88,
    aggroRange: 700,
    preferredRange: 0,
    attack: { range: 54, cooldown: 1.9, windup: 0.55 },
    base: { maxHp: 150, attack: 22, defense: 14 },
    perLevel: { maxHp: 34, attack: 4.0, defense: 3.2 },
    xpBase: 60,
  },
  harpy: {
    id: 'harpy',
    name: 'Harpy',
    behavior: 'ranged',
    color: '#3fa6a0',
    accent: '#bff0ea',
    radius: 18,
    speed: 168,
    aggroRange: 820,
    preferredRange: 300,
    attack: {
      range: 420,
      cooldown: 1.6,
      windup: 0.35,
      projectile: { speed: 520, radius: 6, life: 1.2, color: '#8fe3d0' },
    },
    base: { maxHp: 90, attack: 20, defense: 3 },
    perLevel: { maxHp: 22, attack: 3.8, defense: 1.0 },
    xpBase: 46,
  },
  ogre: {
    id: 'ogre',
    name: 'Ogre',
    behavior: 'melee',
    color: '#6f7a3a',
    accent: '#cbd68a',
    radius: 32,
    speed: 104,
    aggroRange: 780,
    preferredRange: 0,
    attack: { range: 58, cooldown: 1.8, windup: 0.5 },
    base: { maxHp: 170, attack: 30, defense: 10 },
    perLevel: { maxHp: 38, attack: 5.0, defense: 2.6 },
    xpBase: 72,
  },
  wraith: {
    id: 'wraith',
    name: 'Wraith',
    behavior: 'ranged',
    color: '#7fd0e8',
    accent: '#e6fbff',
    radius: 17,
    speed: 150,
    aggroRange: 860,
    preferredRange: 360,
    attack: {
      range: 460,
      cooldown: 1.8,
      windup: 0.45,
      projectile: { speed: 480, radius: 8, life: 1.4, color: '#bff0ff' },
    },
    base: { maxHp: 75, attack: 26, defense: 1 },
    perLevel: { maxHp: 17, attack: 5.0, defense: 0.7 },
    xpBase: 58,
  },
  dragonkin: {
    id: 'dragonkin',
    name: 'Dragonkin',
    behavior: 'melee',
    color: '#a83b32',
    accent: '#f0c060',
    radius: 34,
    speed: 116,
    aggroRange: 900,
    preferredRange: 0,
    attack: { range: 66, cooldown: 2.0, windup: 0.6 },
    base: { maxHp: 210, attack: 44, defense: 20 },
    perLevel: { maxHp: 46, attack: 7.0, defense: 4.0 },
    xpBase: 130,
  },
  /**
   * A world boss. Never rolled by the normal spawn tables - the game places
   * these by hand at a handful of landmarks, gives them a name, and lets them
   * lurk until an adventurer walks close.
   */
  boss: {
    id: 'boss',
    name: 'World Boss',
    behavior: 'melee',
    color: '#8b2f3a',
    accent: '#ffd166',
    radius: 48,
    speed: 98,
    aggroRange: 1200,
    preferredRange: 0,
    attack: { range: 104, cooldown: 2.4, windup: 0.78 },
    base: { maxHp: 2200, attack: 34, defense: 18 },
    perLevel: { maxHp: 430, attack: 4.4, defense: 2.6 },
    xpBase: 120,
  },
};

/**
 * Spawn table by distance band from the world centre. Early bands are easy
 * melee fodder; later bands mix in ranged threats and tanky brutes.
 */
export interface SpawnEntry {
  kind: EnemyKind;
  weight: number;
}

export function spawnTableForLevel(level: number): SpawnEntry[] {
  if (level <= 2) return [{ kind: 'slime', weight: 8 }, { kind: 'boar', weight: 2 }];
  if (level <= 6) {
    return [
      { kind: 'slime', weight: 5 },
      { kind: 'boar', weight: 4 },
      { kind: 'wolf', weight: 2 },
    ];
  }
  if (level <= 11) {
    return [
      { kind: 'boar', weight: 4 },
      { kind: 'wolf', weight: 4 },
      { kind: 'imp', weight: 3 },
      { kind: 'slime', weight: 1 },
    ];
  }
  if (level <= 17) {
    return [
      { kind: 'wolf', weight: 4 },
      { kind: 'imp', weight: 4 },
      { kind: 'wisp', weight: 3 },
      { kind: 'boar', weight: 2 },
    ];
  }
  if (level <= 24) {
    return [
      { kind: 'imp', weight: 4 },
      { kind: 'wisp', weight: 3 },
      { kind: 'skeleton', weight: 3 },
      { kind: 'wolf', weight: 2 },
    ];
  }
  if (level <= 33) {
    return [
      { kind: 'skeleton', weight: 4 },
      { kind: 'cultist', weight: 4 },
      { kind: 'imp', weight: 2 },
      { kind: 'wisp', weight: 2 },
    ];
  }
  if (level <= 44) {
    return [
      { kind: 'skeleton', weight: 4 },
      { kind: 'cultist', weight: 4 },
      { kind: 'brute', weight: 3 },
      { kind: 'wisp', weight: 2 },
    ];
  }
  if (level <= 56) {
    return [
      { kind: 'cultist', weight: 4 },
      { kind: 'brute', weight: 3 },
      { kind: 'golem', weight: 3 },
      { kind: 'skeleton', weight: 2 },
    ];
  }
  if (level <= 68) {
    return [
      { kind: 'golem', weight: 4 },
      { kind: 'ogre', weight: 3 },
      { kind: 'cultist', weight: 2 },
      { kind: 'harpy', weight: 2 },
    ];
  }
  if (level <= 80) {
    return [
      { kind: 'ogre', weight: 4 },
      { kind: 'harpy', weight: 4 },
      { kind: 'golem', weight: 2 },
      { kind: 'wraith', weight: 2 },
    ];
  }
  if (level <= 90) {
    return [
      { kind: 'harpy', weight: 4 },
      { kind: 'wraith', weight: 4 },
      { kind: 'ogre', weight: 3 },
      { kind: 'dragonkin', weight: 2 },
    ];
  }
  return [
    { kind: 'dragonkin', weight: 5 },
    { kind: 'wraith', weight: 4 },
    { kind: 'ogre', weight: 3 },
    { kind: 'golem', weight: 2 },
  ];
}

/**
 * Which monsters a landmark favours. Distance still sets the level, so these
 * only ever reweight the kinds the level band already allows - a level-2 camp
 * is still boars and slimes, while a high-level graveyard leans hard into the
 * wraiths and brutes that fit its mood.
 */
const POI_THEMES: Record<PoiKind, EnemyKind[]> = {
  camp: ['wolf', 'brute', 'boar'],
  hamlet: ['slime', 'boar'],
  graveyard: ['skeleton', 'wraith', 'wisp'],
  chapel: ['cultist', 'wraith', 'wisp'],
  ruins: ['golem', 'skeleton', 'brute'],
  shrine: ['wisp', 'wraith'],
  farmstead: ['boar', 'slime'],
  watchtower: ['harpy', 'imp', 'wolf'],
};

/** How much more likely a themed monster is inside its landmark's den. */
const POI_THEME_WEIGHT = 3;

export function spawnTableForPoi(kind: PoiKind, level: number): SpawnEntry[] {
  const favoured = POI_THEMES[kind];
  return spawnTableForLevel(level).map((entry) => ({
    kind: entry.kind,
    weight: entry.weight * (favoured.includes(entry.kind) ? POI_THEME_WEIGHT : 1),
  }));
}

export function pickSpawnKind(level: number, roll: number, theme?: PoiKind): EnemyKind {
  const table = theme ? spawnTableForPoi(theme, level) : spawnTableForLevel(level);
  const total = table.reduce((sum, e) => sum + e.weight, 0);
  let target = roll * total;
  for (const entry of table) {
    target -= entry.weight;
    if (target <= 0) return entry.kind;
  }
  return table[table.length - 1].kind;
}

/** XP a monster of this kind and level is worth. Shared so quests can scale with it. */
export function enemyXpValue(def: EnemyDef, level: number): number {
  return Math.round(def.xpBase * Math.pow(level, 1.18));
}

/** Average coins a monster of this kind and level drops. */
export function enemyCoinValue(def: EnemyDef, level: number): number {
  return def.xpBase * COINS_PER_XP * Math.pow(level, COIN_LEVEL_EXP);
}

/**
 * The monster level the wilderness is tuned to at a given spot. Both the spawn
 * logic and the HUD "zone" readout use this, so what the game shows you always
 * matches what it actually spawns.
 */
export function zoneLevelAt(x: number, y: number): number {
  const distance = Math.hypot(x - CITY_CENTER.x, y - CITY_CENTER.y);
  const beyond = Math.max(0, distance - LEVEL_RAMP_START);
  return Math.min(MAX_ENEMY_LEVEL, 1 + Math.floor(beyond / LEVEL_RAMP_STEP));
}
